// src/lib/apiHostingAgreements.js
import { http } from "./axios";

const getId = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number" || typeof value === "string") return value;
  if (typeof value === "object" && "id" in value) return value.id;
  return null;
};

const isCancelledStatus = (status) => String(status || "").toUpperCase().includes("CANCEL");

const extractList = (response) => {
  const payload = response?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

async function cleanupHostedArtworkOnCancellation(agreement) {
  const artworkId = getId(agreement?.artwork) || agreement?.artwork_id;
  const hostId = getId(agreement?.host) || agreement?.host_id;

  if (artworkId) {
    try {
      await http.patch(`/artworks/${artworkId}/`, { status: "available" });
    } catch (err) {
      console.warn("Unable to set artwork status to available after agreement cancellation", err);
    }
  }

  try {
    const hostedRes = await http.get("/hosts/hosted_artworks/", {
      params: hostId ? { id: hostId } : undefined,
    });
    const hostedRows = extractList(hostedRes);
    const hostedRow = hostedRows.find((row) => {
      const rowArtworkId = getId(row?.artwork) || row?.artwork_id;
      const rowHostId = getId(row?.host) || row?.host_id;
      const artworkMatch = artworkId ? String(rowArtworkId) === String(artworkId) : false;
      const hostMatch = hostId ? String(rowHostId) === String(hostId) : true;
      return artworkMatch && hostMatch;
    });

    if (hostedRow?.id) {
      try {
        await http.delete(`/hosts/hosted_artworks/${hostedRow.id}/`);
      } catch (err) {
        try {
          await http.delete(`/host-artworks/${hostedRow.id}/`);
        } catch (fallbackErr) {
          console.warn("Unable to delete host artwork row after agreement cancellation", fallbackErr || err);
        }
      }
    }
  } catch (err) {
    console.warn("Unable to fetch hosted artworks for cancellation cleanup", err);
  }
}

export function createHostOffer(artworkId, terms, options = {}) {
  const payload = {
    artwork_id: artworkId,
    terms,
    ...terms,
  };
  if (options.hostId !== undefined && options.hostId !== null) {
    payload.host_id = options.hostId;
  }
  if (options.originalAgreementId !== undefined && options.originalAgreementId !== null) {
    payload.original_agreement_id = options.originalAgreementId;
  }
  if (options.isGalleryRepresentation !== undefined) {
    payload.is_gallery_representation = Boolean(options.isGalleryRepresentation);
  }
  return http.post("/host-requests/offer/", payload);
}

export function createReplacementHostOffer(artworkId, originalAgreementId, terms, options = {}) {
  return createHostOffer(artworkId, terms, {
    ...options,
    originalAgreementId: originalAgreementId,
  });
}

export function counterofferArtworkAgreement(agreementId, patchTerms) {
  return http.patch(`/artwork-agreements/${agreementId}/`, {
    terms: patchTerms,
    ...patchTerms,
  });
}

export function acceptArtworkAgreement(agreementId) {
  return http.post(`/artwork-agreements/${agreementId}/accept/`);
}

export function rejectArtworkAgreement(agreementId) {
  return http.post(`/artwork-agreements/${agreementId}/reject/`);
}

export function requestCancelArtworkAgreement(agreementId) {
  return http.post(`/artwork-agreements/${agreementId}/request-cancel/`);
}

export async function confirmCancelArtworkAgreement(agreementId) {
  const response = await http.post(`/artwork-agreements/${agreementId}/confirm-cancel/`);
  const agreement = response?.data || {};

  if (isCancelledStatus(agreement?.status)) {
    await cleanupHostedArtworkOnCancellation(agreement);
  }

  return response;
}

export function rejectCancelArtworkAgreement(agreementId) {
  return http.patch(`/artwork-agreements/${agreementId}/`, { status: "ACCEPTED" });
}

export function markArtworkAgreementExpired(agreementId) {
  return http.patch(`/artwork-agreements/${agreementId}/`, { status: "EXPIRED" });
}

export function markArtworkAgreementSold(agreementId) {
  return http.post(`/artwork-agreements/${agreementId}/mark-sold/`);
}

export function listMyArtworkAgreements(params = {}) {
  return http.get("/artwork-agreements/", { params });
}

export function getArtworkAgreement(agreementId) {
  return http.get(`/artwork-agreements/${agreementId}/`);
}

export async function markHostedAgreementSold(artworkId) {
  const res = await listMyArtworkAgreements({ artwork: artworkId });
  const list = Array.isArray(res.data) ? res.data : res.data?.results || [];
  const accepted = list.find((item) => String(item?.status || "").toUpperCase().includes("ACCEPT"));
  if (!accepted?.id) return;
  await http.patch(`/artwork-agreements/${accepted.id}/`, { status: "SOLD" });
}
